import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Loader2, Upload, Trash2, Save, Images, Replace } from "lucide-react";
import { toast } from "sonner";
import client from "@/api/client";

function resolveLogoUrl(url) {
  if (!url) return "";
  if (/^https?:\/\//i.test(url)) return url;
  const backend = process.env.REACT_APP_BACKEND_URL || "";
  if (url.startsWith("/api/")) return `${backend}${url}`;
  return url;
}

export function AdminMarqueeManager() {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(null);
  const [newName, setNewName] = useState("");
  const [newLink, setNewLink] = useState("");
  const fileRef = useRef(null);
  const replaceRefs = useRef({});
  const [replacing, setReplacing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await client.get("/admin/marquee/logos");
      setItems(res.data?.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("name", newName || "");
      fd.append("link", newLink || "");
      fd.append("order", String(items.length));
      await client.post("/admin/marquee/logos", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setNewName("");
      setNewLink("");
      if (fileRef.current) fileRef.current.value = "";
      toast.success(t("marquee.logoUploaded"));
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("common.error"));
    } finally {
      setUploading(false);
    }
  };

  const patchItem = (id, patch) => {
    setItems((prev) => prev.map((it) => (it.logo_id === id ? { ...it, ...patch } : it)));
  };

  const saveItem = async (item) => {
    setSaving(item.logo_id);
    try {
      await client.patch(`/admin/marquee/logos/${item.logo_id}`, {
        name: item.name || "",
        link: item.link || "",
        order: Number(item.order) || 0,
      });
      toast.success(t("marquee.logoUpdated"));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error"));
    } finally {
      setSaving(null);
    }
  };

  const deleteItem = async (id) => {
    try {
      await client.delete(`/admin/marquee/logos/${id}`);
      toast.success(t("marquee.logoDeleted"));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error"));
    }
  };

  const handleReplaceImage = async (id, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setReplacing(id);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await client.post(`/admin/marquee/logos/${id}/image`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      // Reset the input so re-picking the same file re-triggers change.
      if (replaceRefs.current[id]) replaceRefs.current[id].value = "";
      toast.success(t("marquee.imageReplaced"));
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("common.error"));
    } finally {
      setReplacing(null);
    }
  };

  return (
    <Card className="rounded-2xl border border-border p-6 space-y-6" data-testid="admin-panel-marquee">
      <div className="flex items-start gap-3">
        <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
          <Images className="h-5 w-5" />
        </span>
        <div className="min-w-0">
          <h2 className="font-display text-xl font-semibold">{t("marquee.adminSection")}</h2>
          <p className="text-sm text-muted-foreground mt-1">{t("marquee.adminDesc")}</p>
        </div>
      </div>

      {/* Upload row */}
      <div className="rounded-xl border border-border p-4 space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">{t("marquee.nameLabel")}</Label>
            <Input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              className="rounded-lg mt-1"
              placeholder="Ministry of Investment"
              data-testid="admin-marquee-new-name"
            />
          </div>
          <div>
            <Label className="text-xs">{t("marquee.linkLabel")}</Label>
            <Input
              value={newLink}
              onChange={(e) => setNewLink(e.target.value)}
              className="rounded-lg mt-1"
              placeholder="https://…"
              data-testid="admin-marquee-new-link"
            />
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <label className="cursor-pointer">
            <input
              ref={fileRef}
              type="file"
              accept="image/png,image/jpeg,image/svg+xml,image/webp"
              className="hidden"
              onChange={handleUpload}
              data-testid="admin-marquee-upload-input"
            />
            <Button asChild variant="outline" className="rounded-xl gap-2" type="button" disabled={uploading}>
              <span>
                {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}{" "}
                {uploading ? t("marquee.uploading") : t("marquee.chooseImage")}
              </span>
            </Button>
          </label>
          <p className="text-xs text-muted-foreground">{t("marquee.hint")}</p>
        </div>
      </div>

      {/* List */}
      {loading ? (
        <div className="flex justify-center py-10">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      ) : items.length === 0 ? (
        <p className="text-sm text-muted-foreground py-6 text-center" data-testid="admin-marquee-empty">
          {t("marquee.empty")}
        </p>
      ) : (
        <div className="space-y-3">
          {items.map((it) => (
            <div
              key={it.logo_id}
              className="grid grid-cols-1 sm:grid-cols-[80px_1fr_1fr_80px_auto] items-center gap-3 rounded-xl border border-border p-3"
              data-testid={`admin-marquee-row-${it.logo_id}`}
            >
              <div className="flex items-center justify-center rounded-lg bg-muted/40 h-14 px-2 relative">
                <img
                  src={resolveLogoUrl(it.url)}
                  alt={it.name || "logo"}
                  className="h-12 w-auto max-w-[72px] object-contain"
                />
                <label
                  className="group absolute inset-0 flex items-center justify-center rounded-lg opacity-0 hover:opacity-100 focus-within:opacity-100 bg-background/70 cursor-pointer transition-opacity pointer-events-none hover:pointer-events-auto focus-within:pointer-events-auto"
                  title={t("marquee.replaceImage")}
                  data-testid={`admin-marquee-replace-label-${it.logo_id}`}
                >
                  <input
                    ref={(el) => { replaceRefs.current[it.logo_id] = el; }}
                    type="file"
                    accept="image/png,image/jpeg,image/svg+xml,image/webp"
                    className="hidden"
                    onChange={(e) => handleReplaceImage(it.logo_id, e)}
                    data-testid={`admin-marquee-replace-input-${it.logo_id}`}
                  />
                  {replacing === it.logo_id ? (
                    <Loader2 className="h-4 w-4 animate-spin text-primary" />
                  ) : (
                    <Replace className="h-4 w-4 text-primary" />
                  )}
                </label>
              </div>
              <div>
                <Label className="text-xs">{t("marquee.nameLabel")}</Label>
                <Input
                  value={it.name || ""}
                  onChange={(e) => patchItem(it.logo_id, { name: e.target.value })}
                  className="rounded-lg mt-1"
                  data-testid={`admin-marquee-name-${it.logo_id}`}
                />
              </div>
              <div>
                <Label className="text-xs">{t("marquee.linkLabel")}</Label>
                <Input
                  value={it.link || ""}
                  onChange={(e) => patchItem(it.logo_id, { link: e.target.value })}
                  className="rounded-lg mt-1"
                  data-testid={`admin-marquee-link-${it.logo_id}`}
                />
              </div>
              <div>
                <Label className="text-xs">{t("marquee.orderLabel")}</Label>
                <Input
                  type="number"
                  value={it.order ?? 0}
                  onChange={(e) => patchItem(it.logo_id, { order: e.target.value })}
                  className="rounded-lg mt-1"
                  data-testid={`admin-marquee-order-${it.logo_id}`}
                />
              </div>
              <div className="flex items-center gap-1 sm:justify-end">
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={() => saveItem(it)}
                  disabled={saving === it.logo_id}
                  title={t("common.save")}
                  data-testid={`admin-marquee-save-${it.logo_id}`}
                >
                  {saving === it.logo_id ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Save className="h-4 w-4 text-primary" />
                  )}
                </Button>
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={() => deleteItem(it.logo_id)}
                  title={t("admin.delete")}
                  data-testid={`admin-marquee-delete-${it.logo_id}`}
                >
                  <Trash2 className="h-4 w-4 text-destructive" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

export default AdminMarqueeManager;
